import * as React from 'react';
import classNames from 'classnames';

export interface ProgressIndicatorProps
    extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Type of indicator. Either determinate or indeterminate. Default is indeterminate.
     */
    type?: 'determinate' | 'indeterminate';
    progressId?: string;
    progress?: number;
}

/**
 * @name        a-progress-indicator
 * @type        atom
 *
 * @param       type          Type of indicator. Either determinate or indeterminate
 * @param       progressId    identifier for a determinate progress indicator
 * @param       progress      Progress of the indicator
 *
 * @description
 * representation of a progress indicator
 */

export const ProgressIndicator = React.forwardRef<
    HTMLDivElement,
    ProgressIndicatorProps
>(
    (
        {
            type = 'indeterminate',
            progressId,
            progress,
            className,
            ...rest
        }: ProgressIndicatorProps,
        ref,
    ) => {
        const progressIndicatorTypeClass = classNames('a-progress-indicator', {
            [className]: className,
            [`-${type}`]: type,
        });
        const progressValue: number = progress != null ? progress : 0;
        const progressStyle = { width: `${progressValue}%` };
        if (type === 'determinate') {
            return (
                <div
                    className={progressIndicatorTypeClass}
                    id={progressId}
                    role="progressbar"
                    ref={ref}
                    {...rest}
                >
                    <div
                        className="a-progress-indicator__inner-bar"
                        style={progressStyle}
                    />
                </div>
            );
        }
        return (
            <div
                className={progressIndicatorTypeClass}
                role="progressbar"
                ref={ref}
                {...rest}
            >
                <div className="a-progress-indicator__anim-bar">
                    <div className="a-progress-indicator__inner-bar" />
                </div>
            </div>
        );
    },
);

ProgressIndicator.displayName = 'ProgressIndicator';
